import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Chip,
  IconButton,
  Tooltip,
  Typography,
  makeStyles,
} from "@material-ui/core";
import {
  InsertDriveFile,
  PictureAsPdf,
  VideoLibrary,
  MusicNote,
  Image as ImageIcon,
  DeleteOutline,
  PlayArrow,
  OpenInNew,
} from "@material-ui/icons";
import api from "../../services/api";

const KIND_META = {
  image: { label: "Imagen", color: "#0284c7", bg: "rgba(2, 132, 199, 0.12)" },
  video: { label: "Video", color: "#7c3aed", bg: "rgba(124, 58, 237, 0.12)" },
  audio: { label: "Audio", color: "#d97706", bg: "rgba(217, 119, 6, 0.12)" },
  pdf: { label: "PDF", color: "#dc2626", bg: "rgba(220, 38, 38, 0.12)" },
  file: { label: "Archivo", color: "#64748b", bg: "rgba(100, 116, 139, 0.12)" },
};

const useStyles = makeStyles((theme) => {
  const isDark = theme.mode === "dark" || theme.palette.type === "dark";
  return {
    card: {
      display: "flex",
      alignItems: "center",
      gap: 12,
      width: "100%",
      maxWidth: 460,
      padding: 8,
      borderRadius: 12,
      border: `1px solid ${isDark ? "rgba(148, 163, 184, 0.18)" : "rgba(15, 23, 42, 0.08)"}`,
      backgroundColor: isDark ? "rgba(15, 23, 42, 0.45)" : "#fff",
      boxShadow: isDark
        ? "0 1px 2px rgba(0,0,0,0.25)"
        : "0 1px 2px rgba(15, 23, 42, 0.04), 0 4px 12px rgba(15, 23, 42, 0.04)",
      transition: "border-color 0.15s ease, box-shadow 0.15s ease",
      "&:hover": {
        borderColor: isDark ? "rgba(148, 163, 184, 0.35)" : "rgba(15, 23, 42, 0.14)",
        boxShadow: isDark
          ? "0 4px 16px rgba(0,0,0,0.28)"
          : "0 6px 18px rgba(15, 23, 42, 0.08)",
      },
    },
    thumb: {
      position: "relative",
      flexShrink: 0,
      width: 72,
      height: 72,
      borderRadius: 8,
      overflow: "hidden",
      backgroundColor: isDark ? "#0f172a" : "#f1f5f9",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    compactThumb: {
      position: "relative",
      width: 40,
      height: 40,
      borderRadius: 8,
      overflow: "hidden",
      backgroundColor: isDark ? "#0f172a" : "#f8fafc",
      border: `1px solid ${isDark ? "rgba(148, 163, 184, 0.16)" : "rgba(15, 23, 42, 0.08)"}`,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      verticalAlign: "middle",
      cursor: "pointer",
    },
    media: {
      width: "100%",
      height: "100%",
      objectFit: "cover",
      display: "block",
    },
    playBadge: {
      position: "absolute",
      inset: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: "linear-gradient(180deg, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.35) 100%)",
      pointerEvents: "none",
    },
    playIcon: {
      width: 28,
      height: 28,
      borderRadius: "50%",
      backgroundColor: "rgba(255,255,255,0.92)",
      color: "#0f172a",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
    },
    info: {
      minWidth: 0,
      flex: 1,
      display: "flex",
      flexDirection: "column",
      gap: 4,
    },
    name: {
      fontWeight: 600,
      fontSize: 13,
      lineHeight: 1.35,
      color: theme.palette.text.primary,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    metaRow: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      flexWrap: "wrap",
    },
    hint: {
      fontSize: 11,
      color: theme.palette.text.secondary,
    },
    actions: {
      display: "flex",
      alignItems: "center",
      gap: 2,
      flexShrink: 0,
    },
    iconTile: {
      width: "100%",
      height: "100%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    list: {
      display: "flex",
      flexWrap: "wrap",
      gap: 8,
      marginTop: 10,
      padding: 10,
      borderRadius: 12,
      border: `1px solid ${isDark ? "rgba(148, 163, 184, 0.16)" : "rgba(15, 23, 42, 0.08)"}`,
      backgroundColor: isDark ? "rgba(15, 23, 42, 0.28)" : "#f8fafc",
    },
    listLabel: {
      width: "100%",
      fontSize: 11,
      fontWeight: 600,
      letterSpacing: 0.3,
      textTransform: "uppercase",
      color: theme.palette.text.secondary,
      marginBottom: 2,
    },
  };
});

const IMAGE_EXT = ["jpg", "jpeg", "png", "gif", "bmp", "webp", "svg"];
const VIDEO_EXT = ["mp4", "webm", "mov", "avi", "mkv", "3gp"];
const AUDIO_EXT = ["mp3", "wav", "ogg", "m4a", "aac", "flac", "opus"];
const PDF_EXT = ["pdf"];

const getExtension = (name = "") => {
  const clean = String(name).split("?")[0].split("#")[0];
  const parts = clean.split(".");
  return parts.length > 1 ? parts.pop().toLowerCase() : "";
};

export const buildPublicMediaUrl = (mediaPath, fileListId) => {
  if (!mediaPath) return "";
  const value = String(mediaPath).replace(/\\/g, "/");
  if (value.startsWith("blob:") || value.startsWith("http://") || value.startsWith("https://")) {
    return value;
  }
  const backend = process.env.REACT_APP_BACKEND_URL || "";
  const fileName = value.split("/").filter(Boolean).pop();
  if (fileListId) {
    return `${backend}/public/fileList/${fileListId}/${fileName}`;
  }
  const publicIndex = value.indexOf("/public/");
  if (publicIndex >= 0) {
    return `${backend}${value.slice(publicIndex)}`;
  }
  if (value.startsWith("/home/") || value.startsWith("/var/") || /^[A-Za-z]:\//.test(value)) {
    return `${backend}/public/${fileName}`;
  }
  return `${backend}/public/${value.replace(/^\/+/, "")}`;
};

const detectKind = ({ file, mediaName, mediaPath, mediaType }) => {
  const mime = file?.type || mediaType || "";
  if (mime.startsWith("image/")) return "image";
  if (mime.startsWith("video/")) return "video";
  if (mime.startsWith("audio/")) return "audio";
  if (mime === "application/pdf") return "pdf";
  const ext = getExtension(mediaName || file?.name || mediaPath || "");
  if (IMAGE_EXT.includes(ext)) return "image";
  if (VIDEO_EXT.includes(ext)) return "video";
  if (AUDIO_EXT.includes(ext)) return "audio";
  if (PDF_EXT.includes(ext)) return "pdf";
  return "file";
};

const formatSize = (bytes) => {
  const size = Number(bytes);
  if (!Number.isFinite(size) || size <= 0) return "";
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

const KindIcon = ({ kind, fontSize = 22 }) => {
  const color = KIND_META[kind]?.color || KIND_META.file.color;
  const props = { style: { fontSize, color } };
  if (kind === "image") return <ImageIcon {...props} />;
  if (kind === "video") return <VideoLibrary {...props} />;
  if (kind === "audio") return <MusicNote {...props} />;
  if (kind === "pdf") return <PictureAsPdf {...props} />;
  return <InsertDriveFile {...props} />;
};

const MediaThumb = ({ kind, src, name, compact, classes, onOpen }) => {
  const [broken, setBroken] = useState(false);
  const showVisual = Boolean(src) && !broken && (kind === "image" || kind === "video");

  return (
    <Box
      className={compact ? classes.compactThumb : classes.thumb}
      onClick={onOpen}
      style={{ cursor: src ? "pointer" : "default", backgroundColor: KIND_META[kind]?.bg }}
    >
      {showVisual && kind === "image" && (
        <img src={src} alt={name} className={classes.media} onError={() => setBroken(true)} />
      )}
      {showVisual && kind === "video" && (
        <video
          src={src}
          className={classes.media}
          muted
          playsInline
          preload="metadata"
          onError={() => setBroken(true)}
        />
      )}
      {(!showVisual) && (
        <Box className={classes.iconTile}>
          <KindIcon kind={kind} fontSize={compact ? 18 : 26} />
        </Box>
      )}
      {showVisual && kind === "video" && (
        <Box className={classes.playBadge}>
          <Box className={classes.playIcon} style={{ width: compact ? 18 : 28, height: compact ? 18 : 28 }}>
            <PlayArrow style={{ fontSize: compact ? 14 : 18 }} />
          </Box>
        </Box>
      )}
    </Box>
  );
};

const AttachmentPreview = ({
  file = null,
  mediaPath = "",
  mediaName = "",
  mediaType = "",
  fileListId = null,
  size = "default",
  showInfo = true,
  onDelete,
}) => {
  const classes = useStyles();
  const objectUrl = useMemo(() => (file ? URL.createObjectURL(file) : ""), [file]);
  const src = objectUrl || buildPublicMediaUrl(mediaPath, fileListId);
  const name = mediaName || file?.name || "Archivo";
  const kind = detectKind({ file, mediaName: name, mediaPath, mediaType });
  const compact = size === "compact";
  const meta = KIND_META[kind] || KIND_META.file;
  const sizeLabel = formatSize(file?.size);

  useEffect(() => {
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [objectUrl]);

  if (!file && !mediaPath && !src) return null;

  const openFull = (event) => {
    if (event) event.stopPropagation();
    if (src) window.open(src, "_blank", "noopener,noreferrer");
  };

  if (compact) {
    return (
      <Tooltip title={name} placement="top">
        <span>
          <MediaThumb
            kind={kind}
            src={src}
            name={name}
            compact
            classes={classes}
            onOpen={openFull}
          />
        </span>
      </Tooltip>
    );
  }

  return (
    <Box className={classes.card}>
      <MediaThumb
        kind={kind}
        src={src}
        name={name}
        compact={false}
        classes={classes}
        onOpen={openFull}
      />
      {showInfo && (
        <Box className={classes.info}>
          <Typography className={classes.name} title={name}>
            {name}
          </Typography>
          <Box className={classes.metaRow}>
            <Chip
              size="small"
              label={meta.label}
              style={{
                height: 20,
                fontSize: 11,
                fontWeight: 600,
                color: meta.color,
                backgroundColor: meta.bg,
              }}
            />
            {sizeLabel ? (
              <Typography className={classes.hint}>{sizeLabel}</Typography>
            ) : null}
          </Box>
          <Typography className={classes.hint}>Clic para abrir</Typography>
        </Box>
      )}
      <Box className={classes.actions}>
        {src && (
          <Tooltip title="Abrir">
            <IconButton size="small" onClick={openFull}>
              <OpenInNew fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {onDelete && (
          <Tooltip title="Quitar archivo">
            <IconButton size="small" onClick={onDelete}>
              <DeleteOutline fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
};

export const FileListPreview = ({ fileListId }) => {
  const classes = useStyles();
  const [options, setOptions] = useState([]);

  useEffect(() => {
    let cancelled = false;
    if (!fileListId) {
      setOptions([]);
      return undefined;
    }
    api
      .get(`/files/${fileListId}`)
      .then(({ data }) => {
        if (!cancelled) {
          setOptions(Array.isArray(data?.options) ? data.options : []);
        }
      })
      .catch(() => {
        if (!cancelled) setOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [fileListId]);

  if (!fileListId || options.length === 0) return null;

  return (
    <Box className={classes.list}>
      <Typography className={classes.listLabel}>Vista previa de la lista</Typography>
      {options.map((option) => (
        <AttachmentPreview
          key={option.id || option.path}
          mediaPath={option.path}
          mediaName={option.name}
          mediaType={option.mediaType}
          fileListId={fileListId}
          size="compact"
          showInfo={false}
        />
      ))}
    </Box>
  );
};

export default AttachmentPreview;
